import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

type RangeKey = "24h" | "7d" | "28d" | "12mo" | "all";

const RANGE_LABELS: Record<RangeKey, string> = {
  "24h": "24 hours",
  "7d": "7 days",
  "28d": "28 days",
  "12mo": "12 months",
  all: "All time",
};

type StreamRowApi = {
  interactionId?: string;
  createdAt?: string | null;
  trackId?: string;
  trackTitle?: string;
  leadId?: string | null;
  leadLabel?: string;
};

export function AdminStreamListPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const range = useMemo((): RangeKey => {
    const r = searchParams.get("range");
    if (r === "24h" || r === "7d" || r === "28d" || r === "12mo" || r === "all") return r;
    return "24h";
  }, [searchParams]);

  const setRange = (next: RangeKey) => {
    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        p.set("range", next);
        return p;
      },
      { replace: true },
    );
  };

  const scopeTrackIdRaw = searchParams.get("trackId");
  const scopeAlbum = scopeTrackIdRaw == null || scopeTrackIdRaw === "" || scopeTrackIdRaw === "album";
  const [checking, setChecking] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [contactsBaseUrl, setContactsBaseUrl] = useState("");
  const [streams, setStreams] = useState<StreamRowApi[]>([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch("/api/admin/session", {
          method: "GET",
          credentials: "same-origin",
        });
        const data = (await res.json()) as { authenticated?: boolean };
        if (cancelled) return;
        if (!res.ok || !data.authenticated) {
          navigate("/admin/login", { replace: true });
          return;
        }
      } catch {
        if (!cancelled) navigate("/admin/login", { replace: true });
        return;
      }
      if (!cancelled) setChecking(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  useEffect(() => {
    if (checking) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const q = new URLSearchParams({ range });
        if (!scopeAlbum && scopeTrackIdRaw) q.set("trackId", scopeTrackIdRaw);
        const res = await fetch(`/api/admin/streams?${q.toString()}`, {
          credentials: "same-origin",
        });
        const data = (await res.json()) as {
          contactsBaseUrl?: string;
          streams?: StreamRowApi[];
          error?: string;
        };
        if (cancelled) return;
        if (!res.ok) {
          setStreams([]);
          setContactsBaseUrl("");
          setError(data.error ?? "Failed to load streams");
          return;
        }
        setContactsBaseUrl(typeof data.contactsBaseUrl === "string" ? data.contactsBaseUrl : "");
        setStreams(Array.isArray(data.streams) ? data.streams : []);
      } catch {
        if (!cancelled) {
          setStreams([]);
          setContactsBaseUrl("");
          setError("Failed to load streams");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [checking, range, scopeAlbum, scopeTrackIdRaw]);

  if (checking) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <p className="text-gray-400 text-sm">Loading…</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen text-white bg-black">
      <div className="px-6 pt-[max(1.5rem,env(safe-area-inset-top))] pb-[max(1.5rem,env(safe-area-inset-bottom))] max-w-4xl mx-auto space-y-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <Link
            to={`/admin?range=${encodeURIComponent(range)}`}
            className="text-sm text-gray-400 hover:text-white underline-offset-2 hover:underline w-fit"
          >
            ← Back to streaming stats
          </Link>
          <label className="text-sm sm:shrink-0">
            <select
              value={range}
              onChange={(e) => setRange(e.target.value as RangeKey)}
              aria-label="Time range"
              className="rounded-lg bg-white/10 border border-white/20 px-3 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-primary w-full sm:w-auto"
            >
              {(Object.keys(RANGE_LABELS) as RangeKey[]).map((key) => (
                <option key={key} value={key} className="bg-black">
                  {RANGE_LABELS[key]}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="rounded-xl border border-white/15 bg-white/5 overflow-hidden overflow-x-auto">
          <table className="w-full text-left border-collapse text-sm min-w-[32rem]">
            <thead>
              <tr className="border-b border-white/15 text-gray-400">
                <th scope="col" className="py-3 px-4 font-medium">
                  Track
                </th>
                <th scope="col" className="py-3 px-4 font-medium min-w-[10rem]">
                  Lead
                </th>
                <th scope="col" className="py-3 px-4 font-medium w-[14rem]">
                  Time
                </th>
              </tr>
            </thead>
            <tbody>
              {error !== null ? (
                <tr>
                  <td colSpan={3} className="py-6 px-4 text-red-400">
                    {error}
                  </td>
                </tr>
              ) : loading ? (
                <tr>
                  <td colSpan={3} className="py-6 px-4 text-gray-400">
                    Loading…
                  </td>
                </tr>
              ) : streams.length === 0 ? (
                <tr>
                  <td colSpan={3} className="py-6 px-4 text-gray-500">
                    No plays in this range.
                  </td>
                </tr>
              ) : (
                streams.map((row, i) => {
                  const key = row.interactionId ?? `row-${i}`;
                  const timeLabel =
                    row.createdAt != null && row.createdAt.length > 0
                      ? new Date(row.createdAt).toLocaleString(undefined, {
                          dateStyle: "medium",
                          timeStyle: "short",
                        })
                      : "—";
                  const trackTitle = typeof row.trackTitle === "string" ? row.trackTitle : "—";
                  const label = typeof row.leadLabel === "string" ? row.leadLabel : "—";
                  const lid = typeof row.leadId === "string" && row.leadId.length > 0 ? row.leadId : null;
                  const base = contactsBaseUrl.replace(/\/+$/, "");
                  const href = lid !== null && base.length > 0 ? `${base}/contacts/${lid}` : null;

                  return (
                    <tr key={key} className="border-b border-white/[0.06] last:border-b-0">
                      <td className="py-3 px-4 align-middle text-gray-200">{trackTitle}</td>
                      <td className="py-3 px-4 align-middle">
                        {href !== null ? (
                          <a
                            href={href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary underline-offset-2 hover:underline font-medium break-words"
                          >
                            {label}
                          </a>
                        ) : (
                          <span className="text-gray-400">{label}</span>
                        )}
                      </td>
                      <td className="py-3 px-4 align-middle text-gray-300 tabular-nums whitespace-nowrap">
                        {timeLabel}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
